// proceduralLibrary/loops/LoopPanel.jsx
//
// Animated Loops library (DL-063b). A grid of 20 seamless loops. The picker
// preview ANIMATES on an oversized inner layer inside a fixed frame (deck ASPECT
// LAW — motion never reshapes the tile). Selecting a loop applies its STATIC
// POSTER as the wallpaper (deck "wedge = static poster" law; live-surface motion
// is a separate renderer extension — see PROGRESS notes). Recolor via ThemeStrip.
//
// prefers-reduced-motion: previews are frozen (no animation) on ALL loops —
// reactive via the shared useReducedMotion hook (src/hooks/useReducedMotion.js)
// so a mid-session OS setting change is honoured without a reload.

import React, { useMemo, useState, useCallback, memo } from "react";
import { Box, Typography } from "@mui/material";
import { LOOPS, buildLoop, AL_KEYFRAMES } from "./loopDefs";
import { PALETTE_BY_NAME } from "../designs/designGenerators";
import ThemeStrip from "../ThemeStrip";
import useReducedMotion from "../../../hooks/useReducedMotion";
import { colors } from "../../../utils/theme";
import { fontFamily } from "../../../theme/tokens";
import cssAttrSafe from "../../../utils/cssAttrSafe";

const paletteTriple = (name) => {
  const p = PALETTE_BY_NAME[name] || PALETTE_BY_NAME.Violet;
  return [p.light, p.deep, p.accent];
};

// fix-ew-barberpole-wraps-mid-word: a single unbroken word (e.g. "Barberpole")
// has no space/hyphen for `whiteSpace:"normal"` to wrap at, so the earlier fix
// (p13-o4-barberpole-loops-tile-label-cut-by-3px) added `overflowWrap:"anywhere"`
// as a fallback — but that greedily fills line 1 with as many characters as
// fit, which for "Barberpole" left a 1-letter orphan ("e") alone on line 2.
// Insert one zero-width-space break opportunity at the word's midpoint so a
// forced wrap splits it roughly in half instead of at whatever the greedy
// fill happens to land on.
const withMidWordBreak = (label) => {
  if (/[\s-]/.test(label)) return label;
  const mid = Math.ceil(label.length / 2);
  return `${label.slice(0, mid)}\u200B${label.slice(mid)}`;
};

// One loop tile, memoized so a click that only changes `selected` (or leaves
// this tile untouched) skips buildLoop()+dangerouslySetInnerHTML entirely for
// the other 19 tiles instead of rebuilding all 20 previews on every render.
const LoopTile = memo(function LoopTile({ loop, triple, selected, prefersReducedMotion, onSelect }) {
  const built = useMemo(() => buildLoop(loop.id, triple), [loop.id, triple]);
  // Strip the animation for reduced-motion (frozen poster preview).
  const layerStyle = prefersReducedMotion ? `background:${built.poster}` : built.css;
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`${loop.label} — ${loop.motion}`}
      aria-pressed={selected}
      title={`${loop.label} — loops seamlessly`}
      onClick={() => onSelect(loop.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(loop.id);
        }
      }}
      sx={{
        border: selected ? `1px solid ${colors.PrimaryPurple}` : `1px solid ${colors.lightBorder}`,
        // rgba derived from colors.PrimaryPurple #9B5AF8 at 35% — no exact
        // token for this alpha step yet, kept literal.
        boxShadow: selected ? "0 0 0 2px rgba(155,90,248,.35)" : "none",
        borderRadius: "10px",
        overflow: "hidden",
        cursor: "pointer",
        background: colors.white,
        // #C9A8FF has no matching design token yet — kept literal.
        "&:hover": { borderColor: "#C9A8FF" },
        "&:focus-visible": { outline: "none", boxShadow: `0 0 0 2px ${colors.lightPurple}` },
      }}
    >
      {/* ASPECT LAW: fixed frame; oversized inner layer holds all motion.
          Inner span built from internal palette colors only (no user input).
          layerStyle carries `background:url("data:...")` whose DOUBLE quote
          would otherwise close this double-quoted style attr early (blanking
          16/20 previews) — cssAttrSafe swaps it to a single quote (valid CSS,
          G-51-safe). */}
      <Box
        sx={{ position: "relative", height: 48, overflow: "hidden", background: colors.white }}
        dangerouslySetInnerHTML={{
          __html:
            `<span style="position:absolute;left:50%;top:50%;width:150px;height:150px;` +
            `margin:-75px 0 0 -75px;background-repeat:repeat;transform-origin:center;display:block;` +
            `${cssAttrSafe(layerStyle)}"></span>`,
        }}
      />
      <Box sx={{ px: 1, py: 0.5 }}>
        <Typography
          lang="en"
          // Wrap onto up to 2 lines instead of a single-line ellipsis clip —
          // several catalog names ("Drift diagonal", "Waves scroll", "Chevron
          // march") don't fit a 4-col tile's ~70px label width on one line;
          // every current label is <=2 words, so wrapping shows it in full.
          // For the one single-word label ("Barberpole"), withMidWordBreak
          // supplies the only break opportunity `whiteSpace:"normal"` has to
          // wrap at, at the word's midpoint (see fix-ew-barberpole-wraps-mid-word).
          //
          // vy-0914-ew-loops-labels-break-mid-word-1024: the picker panel's
          // host (`.builder-panel-responsive` at tablet/in-grid widths —
          // src/pages/SegmentBuilder/styles.css) applies `overflow-wrap:
          // anywhere; word-break: break-word` to EVERY descendant to stop
          // pathological content overflowing the narrower 320-400px panel.
          // That universal rule reaches this label too, undoing the
          // space-only wrap this component relies on and letting the browser
          // pick ANY character to break "diagonal" at ("diago"/"nal", no
          // hyphen) once the 4-col grid squeezes the label column down to
          // ~31px. `!important` restores space-only wrapping for a multi-word
          // label regardless of host; `hyphens:"auto"` (needs the `lang`
          // attr) lets a single overflowing word still break for real when
          // the box just isn't wide enough — but only at a real syllable
          // boundary with a visible hyphen ("diag-"/"onal"), never a bare
          // mid-word split.
          sx={{
            fontSize: "10.5px",
            fontWeight: 700,
            color: colors.primary,
            fontFamily,
            lineHeight: 1.2,
            whiteSpace: "normal",
            overflowWrap: "normal !important",
            wordBreak: "normal !important",
            hyphens: "auto",
            minHeight: "2.4em",
          }}
        >
          {withMidWordBreak(loop.label)}
        </Typography>
        <Typography
          sx={{ fontSize: "9px", fontWeight: 600, letterSpacing: ".03em", textTransform: "uppercase", color: "#9A9AA0", fontFamily }}
        >
          {loop.motion}
        </Typography>
      </Box>
    </Box>
  );
});

const LoopPanel = ({ onApply, segmentColor, wheelColor }) => {
  const prefersReducedMotion = useReducedMotion();
  const [selectedId, setSelectedId] = useState(null);
  const [paletteName, setPaletteName] = useState("Violet");
  const [matchSource, setMatchSource] = useState(null);

  const triple = useMemo(() => paletteTriple(paletteName), [paletteName]);

  const apply = useCallback(
    (id, tri) => {
      const built = buildLoop(id, tri);
      if (built && onApply) onApply(built.poster);
    },
    [onApply]
  );

  const handleSelect = useCallback(
    (id) => {
      setSelectedId(id);
      setMatchSource(null);
      apply(id, triple);
    },
    [apply, triple]
  );

  const handleTheme = (name, third, source) => {
    if (name) {
      setPaletteName(name);
      setMatchSource(null);
      if (selectedId) apply(selectedId, paletteTriple(name));
    } else if (third && selectedId) {
      // Derived (Segment/Wheel match-pill) recolor: clear the now-stale named
      // palette so ThemeStrip stops highlighting a chip that no longer
      // reflects the applied colors, and record which pill matched so the
      // strip can truthfully mark IT active instead.
      setPaletteName(null);
      setMatchSource(source || "match");
      apply(selectedId, [third.light, third.deep, third.accent]);
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      {/* Shared keyframes for the animated previews (scoped by unique names). */}
      <style>{AL_KEYFRAMES}</style>
      <Typography
        sx={{ fontWeight: "bold", fontSize: "14px", pl: 1, mb: 0.5, fontFamily }}
      >
        Animated patterns
      </Typography>
      <Typography sx={{ fontSize: "11.5px", color: colors.darkGrey, pl: 1, mb: 1, fontFamily }}>
        {LOOPS.length} seamless loops for Segments &amp; Wallpapers.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 1,
          maxHeight: 240,
          overflowY: "auto",
        }}
      >
        {LOOPS.map((loop) => (
          <LoopTile
            key={loop.id}
            loop={loop}
            triple={triple}
            selected={selectedId === loop.id}
            prefersReducedMotion={prefersReducedMotion}
            onSelect={handleSelect}
          />
        ))}
      </Box>

      {selectedId && (
        <ThemeStrip
          label="Loop colors"
          selectedName={paletteName}
          selectedMatch={matchSource}
          onSelect={handleTheme}
          segmentColor={segmentColor}
          wheelColor={wheelColor}
        />
      )}
    </Box>
  );
};

export default LoopPanel;
